'use client';
import 'react-day-picker/dist/style.css';
import '@/styles/day-picker.css';

import { autoPlacement, autoUpdate, offset, useFloating } from '@floating-ui/react';
import { Transition } from '@headlessui/react';
import clsx from 'clsx';
import { format, isFuture, subMonths } from 'date-fns';
import { ko } from 'date-fns/locale';
import dayjs from 'dayjs';
import { Fragment, ReactNode, useRef } from 'react';
import { DateFormatter, DateRange, DayPicker } from 'react-day-picker';
import { useBoolean, useOnClickOutside } from 'usehooks-ts';

import Image from '@/components/v2/Image';
import { useCalendar } from '@/hooks/useCalendar';

import { Button } from '../Button/Button';
import OutlinedButton from '../Button/OutlinedButton';
import RangeHint from './RangeHint';

const MONTHS_SHOWN = 2;

export interface MultiCalendarProps {
  range: DateRange | undefined;
  setRange: (range: DateRange | undefined) => void;
  className?: string;
  /** 기본 날짜 버튼 대신 쓸 트리거. 미지정 시 기존 버튼 */
  children?: ReactNode;
  /** '전체' 프리셋 노출 여부 */
  showAllOption?: boolean;
  /** 선택 가능한 최대 일수 */
  maxRangeDays?: number;
  /** 선택 가능한 최소 일수 */
  minRangeDays?: number;
  /** 선택 가능한 마지막 날짜 */
  latestDate?: Date;
  /** 프리셋 상태 저장 키 */
  storageKey?: string;
  /** 모바일 노출 방식. dropdown 은 트리거 아래에 붙는다 */
  presentation?: 'modal' | 'dropdown';
  gtmIds?: {
    week?: string;
    twoWeek?: string;
    month?: string;
    submit?: string;
  };
}

const MultiCalendar = ({
  range,
  setRange,
  className = '',
  children,
  showAllOption = true,
  maxRangeDays,
  minRangeDays,
  latestDate,
  storageKey,
  gtmIds,
}: MultiCalendarProps) => {
  const lastSelectable = latestDate ?? new Date();
  const ref = useRef<HTMLDivElement>(null);

  const { value: isOpen, setTrue: showModal, setFalse: closeModal, toggle } = useBoolean(false);

  const handleToggle = () => toggle();

  const { refs, floatingStyles } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: showModal,
    middleware: [
      autoPlacement({
        autoAlignment: true,
        allowedPlacements: ['bottom', 'bottom-start', 'bottom-end'],
      }),
      offset(10),
    ],
  });

  const {
    currentOption,
    calendarRange,
    canConfirm,
    isPicking,
    setLast1Week,
    setLast2Week,
    setLast1Month,
    setAll,
    customSetRange,
    closeCalendar,
    confirmRange,
  } = useCalendar({
    range,
    setRange,
    closeModal,
    storageKey,
    minRangeDays,
    latestDate,
  });

  // PC·모바일 캘린더가 같이 마운트돼 프리셋 상태를 공유한다. 닫힌 쪽이 상태를 덮어쓰지 않게 막는다
  useOnClickOutside(ref as any, () => isOpen && closeCalendar());

  // 범위가 완성된 상태에서 min 을 주면 라이브러리가 범위 안쪽을 잠가 다시 고를 수 없다
  const limited = !minRangeDays || isPicking;

  return (
    <>
      <div className={clsx('relative w-fit', className)} ref={ref}>
        <div className="m-1" ref={refs.setReference}>
          {children ? (
            <button type="button" onClick={handleToggle}>
              {children}
            </button>
          ) : (
            <OutlinedButton
              className="px-3.5 h-11 rounded-lg font-normal items-center gap-1.5 lg:min-w-[224px]"
              onClick={handleToggle}
            >
              <Image src={'/img/icons/calendar.svg'} width={20} height={20} alt="calendar" />
              <span className="hidden lg:block w-[186px]">
                {!range?.from && !range?.to ? (
                  <span className="text-gray-400">날짜를 선택해주세요</span>
                ) : (
                  `${range?.from ? dayjs(range?.from).format('YYYY.MM.DD') : '-'} ~ ${
                    range?.to ? dayjs(range?.to).format('YYYY.MM.DD') : dayjs().format('YYYY.MM.DD')
                  }`
                )}
              </span>
            </OutlinedButton>
          )}
        </div>

        <Transition
          show={isOpen}
          as={Fragment}
          enter="transition-opacity duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            key="calendar"
            className="z-30 p-8 rounded-lg border w-max bg-white gap-4"
            style={floatingStyles}
            ref={refs.setFloating}
          >
            <div className="flex gap-2">
              <DayPicker
                locale={ko}
                mode="range"
                selected={calendarRange}
                onSelect={customSetRange}
                defaultMonth={subMonths(lastSelectable, MONTHS_SHOWN - 1)}
                numberOfMonths={MONTHS_SHOWN}
                pagedNavigation
                min={limited ? (minRangeDays ?? 2) : undefined}
                max={limited ? maxRangeDays : undefined}
                captionLayout="dropdown-buttons"
                formatters={{
                  formatCaption,
                }}
                fromYear={2023}
                toYear={new Date().getFullYear()}
                toMonth={lastSelectable}
                disabled={(date) => isFuture(date) || date > lastSelectable}
              />

              <div className="flex p-2 flex-col justify-between flex-shrink-0">
                <div className="flex gap-2 flex-col">
                  <Button
                    $variant={currentOption === 'last1Week' ? 'tonal' : 'text'}
                    $color={currentOption === 'last1Week' ? 'primary' : 'gray'}
                    data-gtm-id={gtmIds?.week}
                    onClick={setLast1Week}
                  >
                    최근 1주
                  </Button>
                  <Button
                    $variant={currentOption === 'last2Week' ? 'tonal' : 'text'}
                    $color={currentOption === 'last2Week' ? 'primary' : 'gray'}
                    data-gtm-id={gtmIds?.twoWeek}
                    onClick={setLast2Week}
                  >
                    최근 2주
                  </Button>
                  <Button
                    $variant={currentOption === 'last1Month' ? 'tonal' : 'text'}
                    $color={currentOption === 'last1Month' ? 'primary' : 'gray'}
                    data-gtm-id={gtmIds?.month}
                    onClick={setLast1Month}
                  >
                    최근 1달
                  </Button>
                  {showAllOption && (
                    <Button
                      $variant={currentOption === 'all' ? 'tonal' : 'text'}
                      $color={currentOption === 'all' ? 'primary' : 'gray'}
                      onClick={setAll}
                    >
                      전체
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <div className="mt-2 flex items-center justify-between gap-4">
              <RangeHint
                range={calendarRange}
                minRangeDays={minRangeDays}
                maxRangeDays={maxRangeDays}
              />
              <div className="ml-auto flex gap-2">
                <Button $color="gray" $variant="tonal" onClick={closeCalendar} className="w-[80px]">
                  취소
                </Button>
                <Button
                  $color="gray"
                  onClick={confirmRange}
                  disabled={!canConfirm}
                  data-gtm-id={gtmIds?.submit}
                  className="w-[80px] disabled:opacity-40"
                >
                  확인
                </Button>
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </>
  );
};

export const formatCaption: DateFormatter = (month, options) => {
  return <>{format(month, 'yyyy년 MM월', { locale: options?.locale })}</>;
};

export default MultiCalendar;
